import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useListState } from '@shared/hooks/useListState';
import PcapFilesTable from './PcapFilesTable';
import { useSelector } from 'react-redux';
import { Pagination, RequestStatusEnum } from '@shared/types';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { PcapFile, PcapFileWithCps } from '../types';
import { getPcapFilesRequest, getPcapFilesReset } from '../store';
import { selectGetPcapFilesError, selectGetPcapFilesStatus, selectPcapFiles } from '../store/selectors';
import { useLibraryChunk } from '../hooks/useLibraryChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { INITIAL_PCAP_FILE_CPS } from '@pages/schemes/constants';

interface PcapFilesListProps {
    dialogMode?: boolean;
    pcapFilesSelectedArr?: PcapFileWithCps[];
    setPcapFilesSelectedArr?: Dispatch<SetStateAction<PcapFileWithCps[]>>;
    pcapFilesUsed?: PcapFileWithCps[];
}

const PcapFilesList = (props: PcapFilesListProps) => (
    <ComponentWithChunks
        featuresArray={['library']}
        hooksArray={[useLibraryChunk]}
        Component={PcapFilesListComponent}
        {...props}
    />
);

const PcapFilesListComponent: FC<PcapFilesListProps> = ({
    dialogMode,
    pcapFilesSelectedArr,
    setPcapFilesSelectedArr,
    pcapFilesUsed,
}) => {
    const {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
    } = useListState('pcapsTable', false, getPcapFilesRequest, { disableUrlParams: true });
    const [selectedPcapFileId, setSelectedPcapFileId] = useState<number | null>(null);
    const pcapFiles = useSelector(selectPcapFiles);
    const getPcapFilesStatus = useSelector(selectGetPcapFilesStatus);
    const { t } = useTranslation();

    const handleChangePagination = (newPagination: Pagination) => {
        setPagination(newPagination);
    };

    const handleClearAllFilters = () => {
        setColumnFilters([]);
    };

    useEffect(() => {
        if (!dialogMode) {
            const rowSelectionKeys = Object.keys(rowSelection);
            // @ts-ignore
            if (rowSelectionKeys.length && rowSelection[rowSelectionKeys[0]]) {
                setSelectedPcapFileId(Number(rowSelectionKeys[0]));
            } else {
                setSelectedPcapFileId(null);
            }
        }
    }, [dialogMode, rowSelection]);

    useEffect(() => {
        const setRowSelectionCallback = (pcapFile: PcapFileWithCps) => {
            setRowSelection((prev) => ({ ...prev, [pcapFile.pk]: true }));
        };
        if (pcapFilesUsed && pcapFilesSelectedArr) {
            pcapFilesUsed?.forEach(setRowSelectionCallback);
            pcapFilesSelectedArr.forEach(setRowSelectionCallback);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (dialogMode && setPcapFilesSelectedArr && pcapFilesUsed) {
            setPcapFilesSelectedArr(() => {
                return Object.keys(rowSelection)
                    .map((id: string) => {
                        const pcapFile = pcapFiles.find((pcapFile: PcapFile) => pcapFile.pk === Number(id));
                        if (pcapFile) {
                            const existItem = pcapFilesUsed.find((item) => item.pk === pcapFile.pk);
                            if (!existItem) {
                                return {
                                    pk: pcapFile.pk,
                                    name: pcapFile.name,
                                    cps: INITIAL_PCAP_FILE_CPS,
                                };
                            }
                        }
                        return null;
                    })
                    .filter(Boolean) as PcapFileWithCps[];
            });
        }
    }, [dialogMode, pcapFiles, pcapFilesUsed, rowSelection, setPcapFilesSelectedArr]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: !dialogMode ? 'calc(100vh - 200px)' : undefined,
                }}
            >
                <PcapFilesTable
                    pcapFiles={pcapFiles}
                    pagination={pagination}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    sorting={sorting}
                    setSorting={setSorting}
                    showColumnFilters={showColumnFilters}
                    setShowColumnFilters={setShowColumnFilters}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    callbacks={{
                        handleChangePagination,
                        handleClearAllFilters,
                    }}
                    toolbarProps={{
                        dialogMode: Boolean(dialogMode),
                        globalFilter,
                        setGlobalFilter,
                        selectedPcapFileId,
                    }}
                    dialogMode={dialogMode}
                    pcapFilesUsed={pcapFilesUsed}
                />
            </Box>

            {getPcapFilesStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('pcap_files_r')}
                    action={t('fetching')}
                    resetActionCallback={getPcapFilesReset}
                    errorSelector={selectGetPcapFilesError}
                />
            )}
        </>
    );
};

export default PcapFilesList;
